import Form, {
  ButtonItem,
  ButtonOptions,
  GroupItem,
  Label,
  RequiredRule,
  SimpleItem,
} from 'devextreme-react/form';
import React, { FormEvent, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { getListApi, putApi } from '../../../apiUtils';
import { Loader } from '../../../components/loader/Loader';
import { useAssetClass } from '../../../contexts/assetClass';
import { useTranslation } from '../../../contexts/translation';
import { useClearButtonProps } from '../../../hooks/useClearButtonProps';
import { Asset } from '../../../types/asset.types';
import { AssetLocation } from '../../../types/assetLocation.types';
import { AssetStatus } from '../../../types/assetStatus.types';
import { AssetType } from '../../../types/assetType.type';
import { API_URL } from '../../../utils/apiUrl';
import './AssetsMobileEditForm.style.scss';

interface FormData {
  parentAssetId: number;
  name: string;
  description: string | null;
  typeId: number;
  inventoryNumber: string | null;
  serialNumber: string | null;
  partNumber: string | null;
  model: string | null;
  centre: string | null;
  producedBySupplierName: string | null;
  assetLocationId: number | null;
  building: string | null;
  room: string | null;
  statusId: number;
}

interface Props {
  assetId: number;
  defaultFormData: FormData;
}

export const AssetsMobileEditForm = ({ assetId, defaultFormData }: Props) => {
  const transFormName = 'AssetsMobile.Form';
  const { translate } = useTranslation();
  const { assetClass } = useAssetClass();
  const selectBoxProps = useClearButtonProps();
  const history = useHistory();

  const [formData, setFormData] = useState<FormData>(defaultFormData);

  const [isLoading, setLoading] = useState(false);

  // Číselníky
  const [topLevelAssets, setTopLevelAssets] = useState<Asset[]>([]);
  const [assetTypes, setAssetTypes] = useState<AssetType[]>([]);
  const [assetLocations, setAssetLocations] = useState<AssetLocation[]>([]);
  const [assetStatuses, setAssetStatuses] = useState<AssetStatus[]>([]);

  useEffect(() => {
    getTopLevelAssets();
    getAssetTypes();
    getAssetLocations();
    getAssetStatuses();
  }, []);

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    setLoading(true);
    putApi({
      url: API_URL.ASSET_PUT,
      data: { ...formData, id: assetId },
      callAfterSuccess: () => setLoading(false),
      callAfterError: () => setLoading(false),
    });
  };

  const getTopLevelAssets = async () => {
    await getListApi<Asset[]>({
      url: `${API_URL.ASSET_LIST_BY_CLASS_TOP_LEVEL}`,
      params: {
        classId: assetClass,
      },
      callAfterSuccess: (result) => {
        setTopLevelAssets(result);
      },
    });
  };

  const getAssetTypes = async () => {
    await getListApi<Asset[]>({
      url: `${API_URL.ASSET_TYPE_LIST_BY_CLASS}`,
      params: {
        classId: assetClass,
      },
      callAfterSuccess: (result) => {
        setAssetTypes(result);
      },
    });
  };

  const getAssetLocations = async () => {
    await getListApi<AssetLocation[]>({
      url: `${API_URL.ASSET_LOCATION_LIST}`,
      params: {},
      callAfterSuccess: (result) => {
        setAssetLocations(result);
      },
    });
  };

  const getAssetStatuses = async () => {
    await getListApi<AssetLocation[]>({
      url: `${API_URL.ASSET_STATUS_LIST}`,
      params: {},
      callAfterSuccess: (result) => {
        setAssetStatuses(result);
      },
    });
  };

  if (isLoading) return <Loader />;

  return (
    <div style={{ padding: 10, backgroundColor: '#f8f8f8', height: '89vh' }}>
      <form action="your-action" onSubmit={handleSubmit}>
        <Form formData={formData} labelLocation={'top'} showValidationSummary>
          <SimpleItem
            cssClass={'input-with-custom-label'}
            dataField={'parentAssetId'}
            editorType={'dxSelectBox'}
            editorOptions={{
              ...selectBoxProps,
              valueExpr: 'id',
              displayExpr: 'name',
              dataSource: topLevelAssets,
            }}
          >
            <Label text={translate!('Line', transFormName)} />
          </SimpleItem>

          <SimpleItem dataField={'name'} cssClass={'input-with-custom-label'}>
            <Label text={translate!('Name', transFormName)} />
            <RequiredRule message={translate!('Name is required', transFormName)} />
          </SimpleItem>

          <SimpleItem
            dataField={'description'}
            editorType="dxTextArea"
            cssClass={'input-with-custom-label'}
          >
            <Label text={translate!('Description', transFormName)} />
          </SimpleItem>

          <SimpleItem
            cssClass={'input-with-custom-label'}
            dataField={'typeId'}
            editorType={'dxSelectBox'}
            editorOptions={{
              valueExpr: 'id',
              displayExpr: 'name',
              dataSource: assetTypes,
            }}
          >
            <Label text={translate!('Type', transFormName)} />
            <RequiredRule message={translate!('Type is required', transFormName)} />
          </SimpleItem>

          <SimpleItem dataField={'inventoryNumber'} cssClass={'input-with-custom-label'}>
            <Label text={translate!('Inventory number', transFormName)} />
          </SimpleItem>

          <SimpleItem dataField={'serialNumber'} cssClass={'input-with-custom-label'}>
            <Label text={translate!('Serial number', transFormName)} />
          </SimpleItem>

          <SimpleItem dataField={'partNumber'} cssClass={'input-with-custom-label'}>
            <Label text={translate!('Part number', transFormName)} />
          </SimpleItem>

          <SimpleItem dataField={'model'} cssClass={'input-with-custom-label'}>
            <Label text={translate!('Model', transFormName)} />
          </SimpleItem>

          <SimpleItem dataField={'centre'} cssClass={'input-with-custom-label'}>
            <Label text={translate!('Centre', transFormName)} />
          </SimpleItem>

          <SimpleItem dataField={'producedBySupplierName'} cssClass={'input-with-custom-label'}>
            <Label text={translate!('Produced by supplierName', transFormName)} />
          </SimpleItem>

          <SimpleItem
            cssClass={'input-with-custom-label'}
            dataField={'assetLocationId'}
            editorType={'dxSelectBox'}
            editorOptions={{
              valueExpr: 'id',
              displayExpr: 'name',
              dataSource: assetLocations,
            }}
          >
            <Label text={translate!('Asset location', transFormName)} />
            <RequiredRule message={translate!('Asset location is required', transFormName)} />
          </SimpleItem>

          <SimpleItem dataField={'building'} cssClass={'input-with-custom-label'}>
            <Label text={translate!('Building', transFormName)} />
          </SimpleItem>

          <SimpleItem dataField={'room'} cssClass={'input-with-custom-label'}>
            <Label text={translate!('Room', transFormName)} />
          </SimpleItem>

          <SimpleItem
            cssClass={'input-with-custom-label'}
            dataField={'statusId'}
            editorType={'dxSelectBox'}
            editorOptions={{
              valueExpr: 'id',
              displayExpr: 'nameTranslated',
              dataSource: assetStatuses,
            }}
          >
            <Label text={translate!('Status', transFormName)} />
            <RequiredRule message={translate!('Status is required', transFormName)} />
          </SimpleItem>

          <GroupItem cssClass={`buttons-container`}>
            <ButtonItem cssClass={'button'}>
              <ButtonOptions
                width={'100%'}
                height={40}
                type={'success'}
                icon="save"
                text={translate!('Save', transFormName)}
                useSubmitBehavior
              />
            </ButtonItem>
            <ButtonItem cssClass={'button'}>
              <ButtonOptions
                text={translate!('Cancel', transFormName)}
                type={'normal'}
                width={'100%'}
                height={40}
                useSubmitBehavior={false}
                onClick={() =>
                  history.push({
                    pathname: '/mobile',
                  })
                }
              />
            </ButtonItem>
          </GroupItem>
        </Form>
      </form>
    </div>
  );
};
